/*
 * CTabs/CTab for Vue 3. The Vue 2 version rendered the default slot twice through "distributors" and
 * found each tab's index via $parent.$children. Here CTabs reads its CTab children from the slot
 * vnodes, renders the nav itself and passes each tab its index.
 */
import { h, Transition, vShow, withDirectives, cloneVNode } from 'vue'
import { CLink, propsFactory } from './link'
import { flattenVNodes } from './utils'

const TABS = 'coreuiTabs'

export const CTab = {
  name: 'CTab',
  inject: { tabs: { from: TABS, default: null } },
  props: { ...propsFactory(), title: String, tabIndex: { type: Number, default: -1 } },
  computed: {
    isActive () {
      return this.tabs && this.tabs.activeTab === this.tabIndex
    }
  },
  render () {
    const fade = this.tabs ? this.tabs.fade : true
    return h(Transition, { name: fade ? 'fade' : '', mode: 'out-in' }, {
      default: () => withDirectives(
        h('div', { class: [this.tabs && this.tabs.addTabClasses, 'tab-pane', { active: this.isActive }] }, this.$slots.default ? this.$slots.default() : undefined),
        [[vShow, this.isActive]]
      )
    })
  }
}

export const CTabs = {
  name: 'CTabs',
  props: {
    fill: Boolean,
    justified: Boolean,
    variant: { type: String, default: 'tabs' },
    fade: { type: Boolean, default: true },
    vertical: [Boolean, Object],
    addNavWrapperClasses: [String, Array, Object],
    addNavClasses: [String, Array, Object],
    addTabsWrapperClasses: [String, Array, Object],
    addTabsClasses: [String, Array, Object],
    addTabClasses: [String, Array, Object],
    activeTab: Number
  },
  emits: ['update:activeTab'],
  provide () {
    const tabs = {}
    Object.defineProperty(tabs, 'activeTab', { get: () => this.activeTabIndex })
    Object.defineProperty(tabs, 'fade', { get: () => this.fade })
    Object.defineProperty(tabs, 'addTabClasses', { get: () => this.addTabClasses })
    return { [TABS]: tabs }
  },
  data () {
    return { activeTabIndex: typeof this.activeTab === 'number' ? this.activeTab : 0 }
  },
  created () {
    this._tabsInitialized = typeof this.activeTab === 'number' // non-reactive flag
  },
  watch: {
    activeTab (val) {
      this.activeTabIndex = val || 0
    }
  },
  computed: {
    gridClasses () {
      if (this.vertical === true) return { navs: 'col-sm-4', content: 'col-sm-8' }
      return this.vertical || {}
    }
  },
  methods: {
    changeTabTo (tab) {
      this.activeTabIndex = tab
      this.$emit('update:activeTab', tab)
    }
  },
  render () {
    const children = flattenVNodes(this.$slots.default ? this.$slots.default() : [])
      .filter((v) => v.type && v.type.name === 'CTab')
    // a tab marked `active` is selected initially when no activeTab prop is given
    if (!this._tabsInitialized) {
      this._tabsInitialized = true
      const idx = children.findIndex((v) => v.props && (v.props.active === '' || v.props.active === true))
      if (idx > 0) this.$nextTick(() => this.changeTabTo(idx))
    }
    const navClasses = [this.addNavClasses, {
      nav: true,
      'nav-tabs': this.variant === 'tabs',
      'nav-pills': this.variant === 'pills',
      'flex-column': this.vertical,
      'nav-fill': this.fill,
      'nav-justified': this.justified,
      'h-100': this.vertical
    }]
    const headers = children.map((vnode, index) => {
      const p = vnode.props || {}
      const disabled = p.disabled === '' || p.disabled === true
      const linkProps = {}
      Object.keys(propsFactory()).forEach((k) => { if (p[k] !== undefined) linkProps[k] = p[k] })
      const titleSlot = vnode.children && vnode.children.title
      return h('li', {
        class: 'nav-item',
        key: index,
        onClick: () => { if (this.activeTabIndex !== index && !disabled) this.changeTabTo(index) }
      }, [h(CLink, { ...linkProps, class: 'nav-link', active: this.activeTabIndex === index, disabled }, {
        default: () => (titleSlot ? titleSlot() : p.title)
      })])
    })
    const panes = children.map((vnode, index) => cloneVNode(vnode, { tabIndex: index, key: index }))
    return h('div', { class: { 'row no-gutters': this.vertical } }, [
      h('div', { class: [this.addNavWrapperClasses, this.gridClasses.navs] }, [h('ul', { class: navClasses }, headers)]),
      h('div', { class: [this.addTabsWrapperClasses, this.gridClasses.content] }, [
        h('div', { class: [this.addTabsClasses, 'tab-content', { 'pl-3 p-0': this.vertical }] }, panes)
      ])
    ])
  }
}
